import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Clock, ArrowLeft, ArrowRight } from "lucide-react";

export default function Tracknn() {
  const contractMilestones = [
    { id: 1, name: "Project Planning & Architecture", status: "COMPLETED", date: "Month 1, Week 1", progress: 100 },
    { id: 2, name: "System Study & Requirement Specs", status: "COMPLETED", date: "Month 1, Week 3", progress: 100 },
    { id: 3, name: "Application Development & Design", status: "IN_PROGRESS", date: "Month 2, Active Stage", progress: 85 },
    { id: 4, name: "Legacy Data Digitization", status: "IN_PROGRESS", date: "Month 2, Active Stage", progress: 70 },
    { id: 5, name: "STQC Security Certification & Audit", status: "SCHEDULED", date: "Post-Build Phase", progress: 40 },
    { id: 6, name: "UAT (User Acceptance Testing)", status: "SCHEDULED", date: "Pre-Go Live Phase", progress: 20 },
    { id: 7, name: "Production Go-Live Launch", status: "SCHEDULED", date: "Milestone Final", progress: 0 },
    { id: 8, name: "Operations & Maintenance (O&M Support)", status: "ACTIVE CONTRACT", date: "4 Years & 6 Months", progress: 100 }
  ];

  return (
    <div className="min-h-screen bg-[#030712] text-slate-200 font-body py-12 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto space-y-8">
        
        {/* HEADER & NAV BACK */}
        <div className="flex items-center justify-between border-b border-white/10 pb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold text-cyan-400 hover:text-cyan-300 font-heading">
            <ArrowLeft size={16} />
            <span>Back to Main Site</span>
          </Link>
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-400 bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/20 font-heading">
            Live Client Portal (Tracknn)
          </span>
        </div>

        {/* CONTRACT METRICS SUMMARY */}
        <div className="glass-panel p-8 rounded-3xl border border-white/10 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <span className="text-xs text-slate-400 uppercase tracking-widest font-heading">Client Contract ID: MYLOOSER11</span>
              <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-white">
                IT Project Milestone <span className="text-gradient-cyan">Status Tracker</span>
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <div className="text-right">
                <span className="text-[11px] text-slate-400 block">Overall Contract Progress</span>
                <span className="font-heading text-2xl font-extrabold text-cyan-400">75% Complete</span>
              </div>
            </div>
          </div>

          {/* PROGRESS METER */}
          <div className="w-full h-3 bg-slate-900 rounded-full overflow-hidden border border-white/10 p-0.5">
            <div className="h-full bg-gradient-to-r from-blue-600 via-cyan-400 to-emerald-400 rounded-full transition-all duration-500 w-[75%]" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs pt-2">
            <div className="glass-card p-3 rounded-xl border border-white/5">
              <span className="text-slate-400 block">Total Budget</span>
              <span className="font-heading font-bold text-white text-sm">₹6 Lakh</span>
            </div>
            <div className="glass-card p-3 rounded-xl border border-white/5">
              <span className="text-slate-400 block">Dedicated Support</span>
              <span className="font-heading font-bold text-white text-sm">₹24,000 / Monthly</span>
            </div>
            <div className="glass-card p-3 rounded-xl border border-white/5">
              <span className="text-slate-400 block">Contract Duration</span>
              <span className="font-heading font-bold text-white text-sm">4 Yrs & 6 Mos</span>
            </div>
            <div className="glass-card p-3 rounded-xl border border-white/5">
              <span className="text-slate-400 block">SLA Support Status</span>
              <span className="font-heading font-bold text-emerald-400 text-sm">24x7 Active</span>
            </div>
          </div>
        </div>

        {/* MILESTONE LIST */}
        <div className="space-y-4">
          <h3 className="font-heading text-lg font-bold text-white">Milestone Deliverables breakdown</h3>

          <div className="space-y-3">
            {contractMilestones.map((m) => (
              <div key={m.id} className="glass-card p-5 rounded-2xl border border-white/10 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-xl text-xs font-bold mt-0.5 ${
                    m.status === "COMPLETED" ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" :
                    m.status === "IN_PROGRESS" ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20" :
                    "bg-slate-900 text-slate-400 border border-white/5"
                  }`}>
                    {m.status === "COMPLETED" ? <CheckCircle2 size={16} /> : <Clock size={16} />}
                  </div>

                  <div>
                    <h4 className="font-heading text-base font-bold text-white">{m.name}</h4>
                    <span className="text-xs text-slate-400">{m.date}</span>
                  </div>
                </div>

                <div className="flex items-center gap-4 w-full md:w-48">
                  <div className="flex-1 h-2 bg-slate-900 rounded-full overflow-hidden border border-white/5">
                    <div className={`h-full rounded-full ${
                      m.status === "COMPLETED" ? "bg-emerald-400" :
                      m.status === "IN_PROGRESS" ? "bg-cyan-400" : "bg-slate-700"
                    }`} style={{ width: `${m.progress}%` }} />
                  </div>
                  <span className="text-xs font-bold text-slate-300 font-heading min-w-[36px] text-right">{m.progress}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FOOTER CTA */}
        <div className="text-center pt-6">
          <Link to="/contact" className="cyber-btn-primary text-xs inline-flex items-center gap-2">
            <span>Contact Dedicated Project Engineer</span>
            <ArrowRight size={14} />
          </Link>
        </div>

      </div>
    </div>
  );
}
